import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { deriveDisplayRole } from '@/lib/organisationStructurePreview';
import { handleApiError } from '../lib/errors';
import {
  type OrganisationStructure,
  type OrganisationStructureInput,
  organisationStructureService,
} from '../services/organisationStructureService';
import { logger } from '../utils/logger';

type FormData = OrganisationStructureInput;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Recorded when a department or team has no shared mailbox at all. */
const NO_EMAIL_PLACEHOLDER = '-';

/**
 * Department and team mailboxes do not always exist, so "-" is accepted as an
 * explicit "none" marker alongside a normal address. An empty value is also
 * allowed because these fields are optional.
 */
function validateOptionalEmail(value: string | undefined | null): string | undefined {
  const v = (value ?? '').trim();
  if (v === '' || v === NO_EMAIL_PLACEHOLDER) return undefined;
  if (!EMAIL_REGEX.test(v)) return 'Enter a valid email address, or "-" if there is none';
  return undefined;
}

const emptyForm: FormData = {
  department_no: '',
  department_name: '',
  team_no: '',
  team_name: '',
  employee_no: '',
  employee_name: '',
  employee_email: '',
  role: '',
  special_role: '',
  type: '',
  department_email: '',
  team_email: '',
  department_pk: '',
  team_pk: '',
  start_date: '',
  end_date: '',
};

function rowToForm(row: OrganisationStructure): FormData {
  return {
    department_no: row.department_no ?? '',
    department_name: row.department_name ?? '',
    team_no: row.team_no ?? '',
    team_name: row.team_name ?? '',
    employee_no: row.employee_no ?? '',
    employee_name: row.employee_name ?? '',
    employee_email: row.employee_email ?? '',
    role: row.role ?? '',
    special_role: row.special_role ?? '',
    type: row.type ?? '',
    department_email: row.department_email ?? '',
    team_email: row.team_email ?? '',
    department_pk: row.department_pk ?? '',
    team_pk: row.team_pk ?? '',
    start_date: row.start_date?.slice(0, 10) ?? '',
    end_date: row.end_date?.slice(0, 10) ?? '',
  };
}

function formToPayload(form: FormData): OrganisationStructureInput {
  const trim = (v: string) => v.trim();
  return {
    department_no: trim(form.department_no ?? '') || undefined,
    department_name: trim(form.department_name ?? '') || undefined,
    team_no: trim(form.team_no ?? '') || undefined,
    team_name: trim(form.team_name ?? '') || undefined,
    employee_no: trim(form.employee_no ?? '') || undefined,
    employee_name: trim(form.employee_name ?? '') || undefined,
    employee_email: trim(form.employee_email ?? '') || undefined,
    role: trim(form.role ?? '') || undefined,
    special_role: trim(form.special_role ?? '') || undefined,
    type: trim(form.type ?? '') || undefined,
    department_email: trim(form.department_email ?? '') || undefined,
    team_email: trim(form.team_email ?? '') || undefined,
    department_pk: trim(form.department_pk ?? '') || undefined,
    team_pk: trim(form.team_pk ?? '') || undefined,
    start_date: form.start_date || undefined,
    end_date: form.end_date || undefined,
  };
}

interface OrganisationStructureDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: OrganisationStructure) => void;
  onEntrySaved?: () => void;
  row?: OrganisationStructure | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const OrganisationStructureDrawer: React.FC<OrganisationStructureDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  row,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && row) {
        setFormData(rowToForm(row));
      } else {
        setFormData(emptyForm);
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, row]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormData, string>> = {
      department_email: validateOptionalEmail(formData.department_email),
      team_email: validateOptionalEmail(formData.team_email),
    };
    setErrors(next);
    return !next.department_email && !next.team_email;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = formToPayload(formData);
      const response =
        isEdit && row
          ? await organisationStructureService.update(row.id, payload)
          : await organisationStructureService.create(payload);
      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Error saving organisation structure row:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;
  const derivedRole = readOnly && row ? deriveDisplayRole(row) : '';

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        readOnly
          ? 'View Organisation Structure'
          : isEdit
            ? 'Edit Organisation Structure'
            : 'Add Organisation Structure'
      }
      description={
        readOnly
          ? 'Organisation structure row details.'
          : isEdit
            ? 'Update the row below.'
            : 'Fill in the organisation structure details.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <p className="text-sm font-medium text-text-secondary">Department</p>
        <TextFormField
          label="Department No"
          value={formData.department_no ?? ''}
          onChange={(v) => handleChange('department_no', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Department Name"
          value={formData.department_name ?? ''}
          onChange={(v) => handleChange('department_name', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Department Email"
          value={formData.department_email ?? ''}
          onChange={(v) => handleChange('department_email', v)}
          error={errors.department_email}
          placeholder={readOnly ? undefined : 'Email address, or "-" if there is none'}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Department PK"
          value={formData.department_pk ?? ''}
          onChange={(v) => handleChange('department_pk', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        {readOnly ? (
          <p className="text-xs text-text-secondary">
            Department lead when employee no matches this PK (dept-level rows with empty team PK).
          </p>
        ) : null}
        <p className="pt-2 text-sm font-medium text-text-secondary">Team</p>
        <TextFormField
          label="Team No"
          value={formData.team_no ?? ''}
          onChange={(v) => handleChange('team_no', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Team Name"
          value={formData.team_name ?? ''}
          onChange={(v) => handleChange('team_name', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Team Email"
          value={formData.team_email ?? ''}
          onChange={(v) => handleChange('team_email', v)}
          error={errors.team_email}
          placeholder={readOnly ? undefined : 'Email address, or "-" if there is none'}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Team PK"
          value={formData.team_pk ?? ''}
          onChange={(v) => handleChange('team_pk', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        {readOnly ? (
          <p className="text-xs text-text-secondary">
            Team lead when employee no matches this PK. Empty means a department-level director seat
            row.
          </p>
        ) : null}
        <p className="pt-2 text-sm font-medium text-text-secondary">Employee</p>
        <TextFormField
          label="Employee No"
          value={formData.employee_no ?? ''}
          onChange={(v) => handleChange('employee_no', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Employee Name"
          value={formData.employee_name ?? ''}
          onChange={(v) => handleChange('employee_name', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Employee Email"
          type="email"
          value={formData.employee_email ?? ''}
          onChange={(v) => handleChange('employee_email', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Role"
          value={formData.role ?? ''}
          onChange={(v) => handleChange('role', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        {readOnly ? (
          <TextFormField
            label="Derived role"
            value={derivedRole || '—'}
            onChange={() => {}}
            disabled
            copyable
          />
        ) : null}
        <TextFormField
          label="Special Role"
          value={formData.special_role ?? ''}
          onChange={(v) => handleChange('special_role', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Type"
          value={formData.type ?? ''}
          onChange={(v) => handleChange('type', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <p className="pt-2 text-sm font-medium text-text-secondary">Dates</p>
        <TextFormField
          label="Start Date"
          type={readOnly ? 'text' : 'date'}
          value={formData.start_date ?? ''}
          onChange={(v) => handleChange('start_date', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="End Date"
          type={readOnly ? 'text' : 'date'}
          value={formData.end_date ?? ''}
          onChange={(v) => handleChange('end_date', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
